import {
  BarChart3,
  Tag,
  Gift,
  ShoppingCart,
  ArrowUpRight,
  Layers,
  Crosshair,
  Link2,
  Users,
  UserPlus,
  Receipt,
  Wallet,
  DollarSign,
  Plug,
  FileBarChart,
  MousePointer,
  UserCog,
  Truck,
  Shield,
  Ship,
  Store,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react';

export type TeamRole = 'admin' | 'viewer' | 'financial' | 'custom';

export type InviteStatus = 'pending' | 'active' | 'expired' | 'revoked';

/** Keys exclusivas do proprietário — não devem aparecer no Team UI nem em presets. */
export const OWNER_ONLY_ACTION_KEYS = [
  'request_normal_withdrawal',
  'request_referral_withdrawal',
  'request_partner_commission_withdrawal',
  'request_anticipation',
  'edit_bank_account',
  'manage_api_keys',
] as const;

const OWNER_ONLY_ACTION_KEY_SET = new Set<string>(OWNER_ONLY_ACTION_KEYS);

export function isOwnerOnlyActionKey(key: string): boolean {
  return OWNER_ONLY_ACTION_KEY_SET.has(key);
}

export function stripOwnerOnlyActionKeys(
  permissions: Record<string, string[]>,
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const [group, keys] of Object.entries(permissions)) {
    const filtered = keys.filter((key) => !isOwnerOnlyActionKey(key));
    if (filtered.length > 0) {
      result[group] = filtered;
    }
  }
  return result;
}

export interface IAccountUserInvite {
  id: number;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
  invite_url: string | null;
}

export interface IAccountUserAccount {
  id: number;
  uuid: string;
  name: string | null;
  email: string;
  last_access?: string | null;
}

export interface ITeamMemberApi {
  uuid: string;
  status: string;
  is_primary?: boolean;
  invited_at: string | null;
  accepted_at: string | null;
  invited_by_account_id: number | null;
  permissions?: Record<string, boolean> | null;
  action_keys?: string[];
  account?: IAccountUserAccount;
  invites?: IAccountUserInvite[];
}

export interface ITeamMember {
  id: string;
  user_id: string | null;
  name: string | null;
  email: string;
  role: TeamRole;
  is_primary: boolean;
  status: InviteStatus;
  permissions: Record<string, string[]>;
  invited_at: string;
  accepted_at: string | null;
  last_access_at: string | null;
  latest_invite_id: string | null;
  latest_invite_url: string | null;
}

const ROLE_LABEL_TO_SLUG: Record<string, TeamRole> = {
  administrador: 'admin',
  admin: 'admin',
  visualizador: 'viewer',
  viewer: 'viewer',
  financeiro: 'financial',
  financial: 'financial',
  personalizado: 'custom',
  custom: 'custom',
};

function resolveRole(raw?: string | null): TeamRole {
  if (!raw) return 'custom';
  return ROLE_LABEL_TO_SLUG[raw.toLowerCase()] ?? 'custom';
}

function resolvePermissions(raw?: Record<string, boolean> | null): Record<string, string[]> {
  if (!raw) return {};
  const result: Record<string, string[]> = {};
  for (const [slug, enabled] of Object.entries(raw)) {
    if (!enabled) continue;
    const dotIndex = slug.indexOf('.');
    const category = dotIndex > 0 ? slug.substring(0, dotIndex) : 'general';
    const actionKey = dotIndex > 0 ? slug.substring(dotIndex + 1) : slug;
    if (!result[category]) result[category] = [];
    result[category].push(actionKey);
  }
  return result;
}

function getLatestPendingInvite(invites?: IAccountUserInvite[]): { id: string | null; url: string | null } {
  if (!invites?.length) return { id: null, url: null };
  const pending = invites
    .filter((i) => !i.accepted_at && !i.revoked_at)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const invite = pending.length > 0 ? pending[0] : invites[invites.length - 1];
  return { id: String(invite.id), url: invite.invite_url ?? null };
}

export function mapApiToTeamMember(raw: ITeamMemberApi): ITeamMember {
  const latestInvite = getLatestPendingInvite(raw.invites);
  return {
    id: raw.uuid,
    user_id: raw.account ? String(raw.account.id) : null,
    name: raw.account?.name ?? null,
    email: raw.account?.email ?? '',
    role: 'custom',
    is_primary: raw.is_primary ?? false,
    status: raw.status as InviteStatus,
    permissions: resolvePermissions(raw.permissions),
    invited_at: raw.invited_at ?? raw.invites?.[0]?.created_at ?? '',
    accepted_at: raw.accepted_at ?? null,
    last_access_at: raw.account?.last_access ?? null,
    latest_invite_id: latestInvite.id,
    latest_invite_url: latestInvite.url,
  };
}

export interface IInviteMemberPayload {
  email: string;
  name?: string;
  permissions: Record<string, string[]>;
}

export interface IUpdatePermissionsPayload {
  permissions: Record<string, string[]>;
}

export const GROUP_ICONS: Record<string, LucideIcon> = {
  metrics: BarChart3,
  products: Tag,
  products_edit: Tag,
  offers: Gift,
  checkouts: ShoppingCart,
  upsells: ArrowUpRight,
  order_bumps: Layers,
  pixels: Crosshair,
  links: Link2,
  coproducers: Users,
  affiliates: UserPlus,
  partner_indications: Users,
  order_details: Receipt,
  wallet: Wallet,
  financial: DollarSign,
  integrations: Plug,
  reports: FileBarChart,
  referral: MousePointer,
  account: UserCog,
  team: Shield,
  orders: Truck,
  shipping: Ship,
  shipping_configurations: Ship,
  marketplace: Store,
  member_area: GraduationCap,
};

export interface PermissionCategoryConfig {
  icon: LucideIcon;
  permissions: string[];
}

export function buildPermissionCategories(
  groupedActions: Record<string, string[]>,
): Record<string, PermissionCategoryConfig> {
  const result: Record<string, PermissionCategoryConfig> = {};
  const sortedGroups = Object.entries(groupedActions).sort(([a], [b]) => a.localeCompare(b));
  for (const [group, keys] of sortedGroups) {
    const permissions = keys
      .filter((key) => !isOwnerOnlyActionKey(key))
      .sort((a, b) => a.localeCompare(b));

    if (permissions.length === 0) {
      continue;
    }

    result[group] = {
      icon: GROUP_ICONS[group] ?? Shield,
      permissions,
    };
  }
  return result;
}

export function buildRolePresets(
  categories: Record<string, PermissionCategoryConfig>,
): Record<Exclude<TeamRole, 'custom'>, Record<string, string[]>> {
  const financialGroups = ['metrics', 'wallet', 'financial', 'reports', 'order_details'];

  const admin: Record<string, string[]> = {};
  const viewer: Record<string, string[]> = {};
  const financial: Record<string, string[]> = {};

  for (const [group, config] of Object.entries(categories)) {
    const assignable = config.permissions.filter((p) => !isOwnerOnlyActionKey(p));
    admin[group] = [...assignable];
    viewer[group] = assignable.filter((p) => p.includes('view'));
    if (financialGroups.includes(group)) {
      financial[group] = [...assignable];
    } else {
      financial[group] = assignable.filter((p) => p.includes('view'));
    }
  }

  return { admin, viewer, financial };
}

export const ROLE_INFO: Record<TeamRole, { emoji: string; color: string }> = {
  admin: { emoji: '⚡', color: '#A9D342' },
  viewer: { emoji: '👁', color: '#60A5FA' },
  financial: { emoji: '💰', color: '#FACC15' },
  custom: { emoji: '🔧', color: '#A1A1AA' },
};

/**
 * Entrega um role baseado nas permissões do membro comparando com presets conhecidos.
 * Deve ser chamado com categorias carregadas (de useTeamActions).
 */
export function deriveRoleFromPermissions(
  memberPermissions: Record<string, string[]>,
  categories: Record<string, PermissionCategoryConfig> | null,
): TeamRole {
  if (!categories || Object.keys(memberPermissions).length === 0) return 'custom';

  const presets = buildRolePresets(categories);

  const matchesPreset = (preset: Record<string, string[]>): boolean => {
    for (const [group, keys] of Object.entries(preset)) {
      const memberKeys = memberPermissions[group] || [];
      if (keys.length !== memberKeys.length) return false;
      const sortedPreset = [...keys].sort();
      const sortedMember = [...memberKeys].sort();
      if (!sortedPreset.every((k, i) => k === sortedMember[i])) return false;
    }
    for (const [group, keys] of Object.entries(memberPermissions)) {
      if (!preset[group] && keys.length > 0) return false;
    }
    return true;
  };

  if (matchesPreset(presets.admin)) return 'admin';
  if (matchesPreset(presets.financial)) return 'financial';
  if (matchesPreset(presets.viewer)) return 'viewer';
  return 'custom';
}
